// 📁 frontend/src/sezioni/MessaggiDettaglio.js
// Vista conversazione singola: chat bubbles + composer.
import React, { useEffect, useRef, useState } from "react";
import { api, buildUrl } from "../api/client";

const STATUS = {
  PENDING:  { label: "In attesa", dot: "bg-yellow-400" },
  ANSWERED: { label: "Risposto",  dot: "bg-green-500"  },
  CLOSED:   { label: "Chiuso",    dot: "bg-gray-400"   },
};

const ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg", "image/jpg", "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "video/mp4",
];
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const POLL_THREAD_MS = 15000;

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function shortTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function buildHeaders(user) {
  const h = {
    "X-Session-Token": user.token,
    "X-User-Id": String(user.id),
  };
  if (user.assunzione_id) h["X-Assunzione-Id"] = String(user.assunzione_id);
  if (user.azienda_id)    h["X-Azienda-Id"]    = String(user.azienda_id);
  return h;
}

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  const isSameDay = (a, b) => a.toDateString() === b.toDateString();
  if (isSameDay(d, today)) return "Oggi";
  if (isSameDay(d, yest)) return "Ieri";
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long", year: "numeric" });
}

function ChatBubbles({ messages, attachmentsByMsg, onDownload }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const wallBg = {
    backgroundColor: "#F1F5F9",
    backgroundImage: "radial-gradient(rgba(47,85,151,0.05) 1px, transparent 1px)",
    backgroundSize: "20px 20px",
  };

  const enriched = messages.map((m, i) => {
    const prev = messages[i - 1];
    const next = messages[i + 1];
    const samePrev = prev && prev.sender_role === m.sender_role;
    const sameNext = next && next.sender_role === m.sender_role;
    const newDay = !prev || (new Date(prev.data_invio).toDateString() !== new Date(m.data_invio).toDateString());
    return { m, samePrev, sameNext, newDay };
  });

  return (
    <div className="flex flex-col px-3 py-3" style={wallBg}>
      {enriched.map(({ m, samePrev, sameNext, newDay }) => {
        const mine = m.sender_role === "EMPLOYEE";
        const atts = attachmentsByMsg[m.id] || [];
        const isClusterStart = !samePrev;
        const isClusterEnd = !sameNext;

        const radius = mine
          ? (isClusterStart ? "rounded-2xl rounded-tr-md" : "rounded-2xl")
          : (isClusterStart ? "rounded-2xl rounded-tl-md" : "rounded-2xl");

        return (
          <React.Fragment key={m.id}>
            {newDay && (
              <div className="flex justify-center my-3">
                <span className="text-[11px] font-semibold text-gray-400">
                  {dayLabel(m.data_invio)}
                </span>
              </div>
            )}
            <div className={`flex ${mine ? "justify-end" : "justify-start"} ${isClusterEnd ? "mb-2" : "mb-0.5"}`}>
              <div
                className={`max-w-[78%] px-3 py-1.5 text-sm shadow-sm ${radius} ${
                  mine
                    ? "bg-blu-uss text-white"
                    : "bg-white text-gray-900 border border-gray-200"
                }`}
              >
                {!mine && isClusterStart && (
                  <div className="text-[11px] font-bold mb-0.5 text-blu-uss">Ufficio HR</div>
                )}
                <div className="whitespace-pre-wrap break-words leading-snug">{m.testo}</div>
                {atts.length > 0 && (
                  <div className="mt-1.5 flex flex-col gap-1">
                    {atts.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => onDownload(a)}
                        className={`flex items-center gap-2 text-xs text-left rounded-md px-2 py-1.5 transition ${
                          mine ? "bg-white/15 hover:bg-white/25" : "bg-gray-100 hover:bg-gray-200"
                        }`}
                        title={a.filename}
                      >
                        <span>📎</span>
                        <span className="flex-1 truncate font-semibold">{a.filename}</span>
                        <span className="opacity-70">{formatBytes(a.size_bytes)}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className={`text-[10px] mt-0.5 flex items-center justify-end gap-1 ${mine ? "text-white/70" : "text-gray-500"}`}>
                  <span>{shortTime(m.data_invio)}</span>
                  {mine && (
                    <span
                      title={m.letto_da_hr_il ? `Letto ${formatDateTime(m.letto_da_hr_il)}` : "Inviato"}
                      style={{ color: m.letto_da_hr_il ? "#7DD3FC" : "rgba(255,255,255,0.6)", fontSize: 12, lineHeight: 1 }}
                    >
                      {m.letto_da_hr_il ? "✓✓" : "✓"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

function Composer({ onSend, sending, allowAttachment, onScrollToBottom }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  function pickFile(e) {
    setError(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > MAX_ATTACHMENT_BYTES) { setError("File troppo grande (max 5 MB)"); fileRef.current.value = ""; return; }
    if (!ALLOWED_MIME.includes(f.type)) { setError("Tipo file non ammesso"); fileRef.current.value = ""; return; }
    setFile(f);
  }

  async function submit() {
    if (!text.trim() && !file) return;
    try {
      await onSend({ testo: text.trim() || (file ? `(allegato: ${file.name})` : ""), file });
      setText(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { setError(err.message || "Errore invio"); }
  }

  const canSend = !sending && (text.trim() || file);
  return (
    <div className="p-2 bg-white border-t border-gray-200">
      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1 mb-1">{error}</div>}
      {file && (
        <div className="text-xs text-gray-800 bg-gray-100 rounded-lg px-2 py-1 mb-1 flex items-center justify-between gap-2 border border-gray-200">
          <span className="truncate">📎 {file.name} <span className="opacity-60">({formatBytes(file.size)})</span></span>
          <button onClick={() => { setFile(null); fileRef.current.value = ""; }} className="text-red-600 font-bold px-1">✕</button>
        </div>
      )}
      <div className="flex items-end gap-2">
        {allowAttachment && (
          <label
            className="cursor-pointer w-10 h-10 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
            title="Allega file"
          >
            📎
            <input ref={fileRef} type="file" className="hidden" onChange={pickFile} accept={ALLOWED_MIME.join(",")} />
          </label>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Messaggio"
          rows={1}
          className="flex-1 resize-none border border-gray-200 rounded-3xl px-4 py-2.5 text-base bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
          style={{ maxHeight: 120 }}
          onFocus={() => setTimeout(() => onScrollToBottom?.(), 300)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
        />
        <button
          disabled={!canSend}
          onClick={submit}
          className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 transition ${
            canSend ? "bg-blu-uss hover:opacity-90" : "bg-gray-400 cursor-not-allowed"
          }`}
          style={{ boxShadow: canSend ? "0 2px 6px rgba(47,85,151,0.35)" : "none" }}
          title="Invia"
        >
          ➤
        </button>
      </div>
    </div>
  );
}

export default function MessaggiDettaglio({ user, threadId }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachmentsByMsg, setAttachmentsByMsg] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const lastSinceRef = useRef(null);
  const messagesContainerRef = useRef(null);

  async function loadAll() {
    try {
      const r = await api.get(`/api/chat/threads/${threadId}`, { headers: buildHeaders(user) });
      setThread(r.thread);
      const m = await api.get(`/api/chat/threads/${threadId}/messages`, { headers: buildHeaders(user) });
      setMessages(m.messages || []);
      const idx = {};
      (m.attachments || []).forEach((a) => { (idx[a.message_id] = idx[a.message_id] || []).push(a); });
      setAttachmentsByMsg(idx);
      const last = (m.messages || []).slice(-1)[0];
      lastSinceRef.current = last ? last.data_invio : new Date().toISOString();
      const lastHr = [...(m.messages || [])].reverse().find((x) => x.sender_role === "HR");
      if (lastHr && !lastHr.letto_da_employee_il) {
        try { await api.post(`/api/chat/messages/${lastHr.id}/read`, {}, { headers: buildHeaders(user) }); } catch (_) { }
      }
    } catch (err) {
      setError(err.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }

  async function pollDelta() {
    if (!lastSinceRef.current) return;
    try {
      const m = await api.get(
        `/api/chat/threads/${threadId}/messages?since=${encodeURIComponent(lastSinceRef.current)}`,
        { headers: buildHeaders(user) }
      );
      const newMsgs = m.messages || [];
      if (!newMsgs.length) return;
      setMessages((prev) => [...prev, ...newMsgs]);
      setAttachmentsByMsg((prev) => {
        const next = { ...prev };
        (m.attachments || []).forEach((a) => { (next[a.message_id] = next[a.message_id] || []).push(a); });
        return next;
      });
      lastSinceRef.current = newMsgs[newMsgs.length - 1].data_invio;
      const lastHr = [...newMsgs].reverse().find((x) => x.sender_role === "HR");
      if (lastHr) {
        try { await api.post(`/api/chat/messages/${lastHr.id}/read`, {}, { headers: buildHeaders(user) }); } catch (_) { }
      }
    } catch (_) { }
  }

  useEffect(() => {
    setLoading(true); setError(null); setThread(null); setMessages([]); setAttachmentsByMsg({}); lastSinceRef.current = null;
    loadAll();
    const iv = setInterval(pollDelta, POLL_THREAD_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [threadId]);

  // Scroll a fondo chat quando la tastiera APRE (viewport si restringe)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let prevHeight = vv.height;
    const onResize = () => {
      const currentHeight = vv.height;
      if (currentHeight < prevHeight) {
        // Aspetta che il flex layout ricalcoli clientHeight col nuovo viewport
        setTimeout(() => {
          const el = messagesContainerRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        }, 150);
      }
      prevHeight = currentHeight;
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  async function downloadAttachment(att) {
    try {
      const res = await fetch(buildUrl(`/api/chat/attachments/${att.id}`), { headers: buildHeaders(user) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Errore download: ${err.message}`);
    }
  }

  async function send({ testo, file }) {
    setSending(true);
    try {
      const r = await api.post(
        `/api/chat/threads/${threadId}/messages`,
        { testo },
        { headers: buildHeaders(user) }
      );
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.postForm(`/api/chat/messages/${r.message_id}/attachments`, fd, { headers: buildHeaders(user) });
      }
      await pollDelta();
    } finally {
      setSending(false);
    }
  }

  const titolo = thread?.created_by === "HR"
    ? (thread?.oggetto || "Comunicazione")
    : (thread?.categoria_nome || "Comunicazione");

  return (
    <div
      className="w-full flex justify-center"
      style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))" }}
    >
      <div className="w-full flex flex-col" style={{ background: "#F1F5F9", height: "100%", paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-800 truncate flex-1">{titolo || "…"}</span>
          {thread && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`w-2.5 h-2.5 rounded-full ${(STATUS[thread.status] || STATUS.PENDING).dot}`} />
              <span className="text-xs text-gray-500">{(STATUS[thread.status] || STATUS.PENDING).label}</span>
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto" ref={messagesContainerRef}>
          {loading && <div className="p-4 text-center text-sm text-gray-700">Caricamento…</div>}
          {error && <div className="p-4 text-sm text-red-700 text-center">{error}</div>}
          {!loading && !error && (
            <ChatBubbles
              messages={messages}
              attachmentsByMsg={attachmentsByMsg}
              onDownload={downloadAttachment}
            />
          )}
        </div>
        {thread?.status !== "CLOSED" && (
          <Composer
            onSend={send}
            sending={sending}
            allowAttachment={true}
            onScrollToBottom={() => {
              const el = messagesContainerRef.current;
              if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
            }}
          />
        )}
        {thread?.status === "CLOSED" && (
          <div className="text-center text-xs p-3 border-t" style={{ background: "#F0F0F0", color: "#6B7280", borderColor: "#D1D5DB" }}>
            🔒 Conversazione chiusa dall'ufficio HR.
          </div>
        )}
      </div>
    </div>
  );
}
